import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Save, Refresh, Email, EmailOutlined } from '@mui/icons-material';
import { Checkbox, FormControlLabel } from '@mui/material';

interface StatusMessage {
  id: string;
  message: string;
  createdAt: string;
  updatedBy: string;
  source?: string;
  isDefault?: boolean;
  scheduledRevertTime?: string;
}

interface GmailStatus {
  gmailConfigured: boolean;
  connectionTest?: {
    success: boolean;
    emailAddress?: string;
    messagesTotal?: number;
    threadsTotal?: number;
    error?: string;
  };
  inboxInfo?: {
    messagesTotal?: number;
    messagesUnread?: number;
  };
  approvedSenders: string[];
}

const StatusPage: React.FC = () => {
  const [currentStatus, setCurrentStatus] = useState<string>('');
  const [isDefault, setIsDefault] = useState<boolean>(false);
  const [scheduledRevertTime, setScheduledRevertTime] = useState<string | null>(null);
  const [newStatus, setNewStatus] = useState<string>('');
  const [defaultStatus, setDefaultStatus] = useState<string>('');
  const [newDefaultStatus, setNewDefaultStatus] = useState<string>('');
  const [statusHistory, setStatusHistory] = useState<StatusMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingDefault, setSavingDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [gmailStatus, setGmailStatus] = useState<GmailStatus | null>(null);
  const [loadingGmailStatus, setLoadingGmailStatus] = useState(false);
  const [currentNZTTime, setCurrentNZTTime] = useState<string>('');
  
  // Widget popup configuration state
  const [popupEnabled, setPopupEnabled] = useState<boolean>(false);
  const [popupMessage, setPopupMessage] = useState<string>('');
  const [savingWidget, setSavingWidget] = useState(false);
  const [widgetEnvironment, setWidgetEnvironment] = useState<string>('staging');

  // Helper function to get the correct base URL
  // ALWAYS use production API - environment is determined by document ID, not API endpoint
  // Detect environment and use correct API URL
  const getBaseUrl = () => {
    const hostname = window.location.hostname;
    const env = hostname.includes('localhost') ? 'staging' : 
                hostname.includes('staging') ? 'staging' : 'production';
    
    if (env === 'staging') {
      console.log('[StatusPage] Using staging API');
      return 'https://us-central1-adrift-chatbot-staging.cloudfunctions.net';
    }
    
    console.log('[StatusPage] Using production API');
    return 'https://us-central1-adriftchatbotv3.cloudfunctions.net';
  };

  // Load current status and history
  const loadStatusData = async () => {
    setLoading(true);
    setError(null);

    try {
      const baseUrl = getBaseUrl();
      
      // Get current status
      const statusResponse = await fetch(`${baseUrl}/getStatusMessage`);
      if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        // If it's the default message, show empty for current status
        // This way the UI clearly shows when there's a custom message vs default
        if (statusData.isDefault) {
          setCurrentStatus('');
        } else {
          setCurrentStatus(statusData.statusMessage || '');
        }
        setIsDefault(statusData.isDefault || false);
        setScheduledRevertTime(statusData.scheduledRevertTime || null);
      }

      // Get default status message
      const defaultResponse = await fetch(`${baseUrl}/getDefaultStatusMessage`);
      if (defaultResponse.ok) {
        const defaultData = await defaultResponse.json();
        setDefaultStatus(defaultData.defaultMessage || '');
        setNewDefaultStatus(defaultData.defaultMessage || '');
      }

      // Get widget configuration
      const hostname = window.location.hostname;
      const env = hostname.includes('localhost') ? 'staging' : hostname.includes('staging') ? 'staging' : 'production';
      setWidgetEnvironment(env);
      
      const widgetResponse = await fetch(`${baseUrl}/getWidgetConfig?environment=${env}`);
      if (widgetResponse.ok) {
        const widgetData = await widgetResponse.json();
        if (widgetData.config) {
          setPopupEnabled(widgetData.config.popup?.enabled || false);
          setPopupMessage(widgetData.config.popup?.message || '');
        }
      }

      // Get status history (we'll need to add this endpoint later)
      // For now, just show current status
      setStatusHistory([]);

    } catch (err) {
      console.error('Error loading status data:', err);
      setError('Failed to load status data');
    } finally {
      setLoading(false);
    }
  };

  // Load Gmail status
  const loadGmailStatus = async () => {
    setLoadingGmailStatus(true);

    try {
      const baseUrl = getBaseUrl();
      const response = await fetch(`${baseUrl}/checkGmailStatus`);

      if (response.ok) {
        const result = await response.json();
        setGmailStatus(result.status);
      } else {
        console.error('Failed to load Gmail status');
      }

    } catch (err) {
      console.error('Error loading Gmail status:', err);
    } finally {
      setLoadingGmailStatus(false);
    }
  };

  // Save new status message
  const saveStatusMessage = async () => {
    if (!newStatus.trim()) {
      setError('Please enter a status message');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const baseUrl = getBaseUrl();
      // Update status message using admin email authorization
      const updateResponse = await fetch(`${baseUrl}/updateStatusMessage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'geoff@emertech.nz',
        },
        body: JSON.stringify({ message: newStatus.trim() }),
      });

      if (!updateResponse.ok) {
        const errorData = await updateResponse.json();
        throw new Error(errorData.message || 'Failed to update status');
      }

      const result = await updateResponse.json();
      
      // Format the reversion time safely
      let reversionTimeText = 'scheduled time';
      if (result.scheduledRevertTime) {
        try {
          const reversionDate = new Date(result.scheduledRevertTime);
          if (!isNaN(reversionDate.getTime())) {
            reversionTimeText = reversionDate.toLocaleString('en-NZ', { 
              timeZone: 'Pacific/Auckland',
              dateStyle: 'full',
              timeStyle: 'short'
            }) + ' NZT';
          }
        } catch (e) {
          console.error('Error formatting reversion time:', e);
        }
      }
      
      setSuccess(`Status message updated successfully! Will revert at ${reversionTimeText}`);
      setCurrentStatus(newStatus.trim());
      setNewStatus('');
      setShowConfirmDialog(false);

      // Reload status data
      await loadStatusData();

    } catch (err) {
      console.error('Error saving status message:', err);
      setError(err instanceof Error ? err.message : 'Failed to save status message');
    } finally {
      setSaving(false);
    }
  };

  // Save default status message
  const saveDefaultStatusMessage = async () => {
    if (!newDefaultStatus.trim()) {
      setError('Please enter a default status message');
      return;
    }

    setSavingDefault(true);
    setError(null);
    setSuccess(null);

    try {
      const baseUrl = getBaseUrl();
      const updateResponse = await fetch(`${baseUrl}/updateDefaultStatusMessage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'geoff@emertech.nz',
        },
        body: JSON.stringify({ message: newDefaultStatus.trim() }),
      });

      if (!updateResponse.ok) {
        const errorData = await updateResponse.json();
        throw new Error(errorData.message || 'Failed to update default status');
      }

      setSuccess('Default status message updated successfully!');
      setDefaultStatus(newDefaultStatus.trim());

      // Reload status data
      await loadStatusData();

    } catch (err) {
      console.error('Error saving default status message:', err);
      setError(err instanceof Error ? err.message : 'Failed to save default status message');
    } finally {
      setSavingDefault(false);
    }
  };

  // Save widget popup configuration
  const saveWidgetConfig = async () => {
    if (popupEnabled && !popupMessage.trim()) {
      setError('Please enter a popup message when enabled');
      return;
    }

    setSavingWidget(true);
    setError(null);
    setSuccess(null);

    try {
      const baseUrl = getBaseUrl();
      const updateResponse = await fetch(`${baseUrl}/updateWidgetConfig`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'geoff@emertech.nz',
        },
        body: JSON.stringify({
          environment: widgetEnvironment,
          popup: {
            enabled: popupEnabled,
            message: popupMessage.trim()
          }
        }),
      });

      if (!updateResponse.ok) {
        const errorData = await updateResponse.json();
        throw new Error(errorData.message || 'Failed to update widget config');
      }

      setSuccess(`Widget popup configuration updated successfully for ${widgetEnvironment}!`);

      // Reload status data
      await loadStatusData();

    } catch (err) {
      console.error('Error saving widget config:', err);
      setError(err instanceof Error ? err.message : 'Failed to save widget configuration');
    } finally {
      setSavingWidget(false);
    }
  };

  // Check for new status update emails
  const checkEmailForUpdates = async () => {
    setCheckingEmail(true);
    setError(null);
    setSuccess(null);

    try {
      const baseUrl = getBaseUrl();
      // Call the email checking endpoint
      const response = await fetch(`${baseUrl}/checkStatusEmails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'geoff@emertech.nz',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to check emails');
      }

      const result = await response.json();
      console.log('Email check result:', result);

      // Handle different response scenarios based on the detailed results
      const { details } = result;

      // Create detailed success message
      let message = `Email check completed. `;

      if (details.emailsFound === 0) {
        message += 'No unread emails found in the inbox.';
      } else {
        message += `Found ${details.emailsFound} unread emails. `;

        if (details.emailsProcessed === 0) {
          message += 'No emails were processed (likely from unapproved senders).';
        } else {
          message += `Processed ${details.emailsProcessed} emails from approved senders.`;

          if (details.statusUpdates > 0) {
            message += ` Successfully updated ${details.statusUpdates} status message(s).`;
          } else {
            message += ' No status updates were found in the processed emails.';
          }
        }
      }

      if (details.errors && details.errors.length > 0) {
        message += ` ${details.errors.length} error(s) occurred during processing.`;
        console.warn('Email processing errors:', details.errors);
      }

      // Only show Gmail not configured error if no emails were processed at all
      if (!details.gmailConfigured && details.emailsProcessed === 0) {
        setError('Gmail API is not configured. Email checking is disabled. Please contact the administrator to set up Gmail API credentials.');
      } else {
        setSuccess(message);
      }

      // Always reload status data to see if any updates were processed
      await loadStatusData();

    } catch (err) {
      console.error('Error checking emails:', err);
      setError(err instanceof Error ? err.message : 'Failed to check emails');
    } finally {
      setCheckingEmail(false);
    }
  };

  useEffect(() => {
    loadStatusData();
    loadGmailStatus();

    // Update current NZT time every second
    const updateTime = () => {
      const now = new Date();
      const nztTime = now.toLocaleString('en-NZ', {
        timeZone: 'Pacific/Auckland',
        dateStyle: 'full',
        timeStyle: 'long'
      });
      setCurrentNZTTime(nztTime);
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleSaveClick = () => {
    if (!newStatus.trim()) {
      setError('Please enter a status message');
      return;
    }
    setShowConfirmDialog(true);
  };

  const handleConfirmSave = async () => {
    await saveStatusMessage();
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        📊 Status Message Management
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Manage the current status message that appears when users click the "Status" button in the chat widget.
        Status messages can also be updated automatically via email.
      </Typography>

      {/* Current NZT Time */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Current NZT Time:</strong> {currentNZTTime}
        </Typography>
      </Alert>

      {/* Schedule Rules */}
      <Alert severity="warning" sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Schedule Rules (All times in NZT):</strong>
        </Typography>
        <Typography variant="body2" component="div">
          • Messages set after 6:00am revert at 6:00am next morning<br/>
          • Latest message always wins (replaces any previous message)<br/>
          • Rules apply to all status updates (manual dashboard updates and email updates)
        </Typography>
      </Alert>

      {/* Current Status */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Current Status Message
          </Typography>
          {loading ? (
            <CircularProgress size={24} />
          ) : currentStatus ? (
            <>
              <Box sx={{ mb: 2 }}>
                {isDefault ? (
                  <Chip label="Using Default Message" color="default" size="small" />
                ) : (
                  <Chip label="Custom Active Message" color="success" size="small" />
                )}
              </Box>
              <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{currentStatus}</Typography>
              </Box>
              {scheduledRevertTime && !isDefault && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Scheduled to revert at:</strong>{' '}
                    {new Date(scheduledRevertTime).toLocaleString('en-NZ', {
                      timeZone: 'Pacific/Auckland',
                      dateStyle: 'full',
                      timeStyle: 'short'
                    })} NZT
                  </Typography>
                </Alert>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No status message set
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Update Status */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Update Status Message
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter a new custom status message. This will replace the default and will automatically revert based on the schedule rules above.
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={3}
            label="New Status Message"
            placeholder="e.g., We are open today and ready for bookings!"
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}
            sx={{ mb: 2 }}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
        </CardContent>
        <CardActions>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSaveClick}
            disabled={saving || !newStatus.trim()}
          >
            {saving ? 'Saving...' : 'Save Status Message'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadStatusData}
            disabled={loading}
          >
            Refresh
          </Button>
        </CardActions>
      </Card>

      {/* Default Status Message Configuration */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Default Status Message Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This is the default message shown when no custom status is active or when a custom status has reverted.
          </Typography>

          {defaultStatus && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Current Default:
              </Typography>
              <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{defaultStatus}</Typography>
              </Box>
            </Box>
          )}

          <TextField
            fullWidth
            multiline
            rows={5}
            label="Default Status Message"
            placeholder="Enter the default status message..."
            value={newDefaultStatus}
            onChange={(e) => setNewDefaultStatus(e.target.value)}
            sx={{ mb: 2 }}
          />
        </CardContent>
        <CardActions>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<Save />}
            onClick={saveDefaultStatusMessage}
            disabled={savingDefault || !newDefaultStatus.trim()}
          >
            {savingDefault ? 'Saving...' : 'Save Default Message'}
          </Button>
        </CardActions>
      </Card>

      {/* Widget Popup Configuration */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Widget Popup Message
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This message appears above the chat widget icon when users first visit the page.
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Current Environment:</strong> {widgetEnvironment}<br/>
              <strong>Firestore Document:</strong> systemConfig/widgetConfig-{widgetEnvironment}
              {widgetEnvironment === 'staging' && (
                <>
                  <br/><br/>
                  <strong>Note:</strong> This configures the popup for LOCAL and STAGING environments. Both are stored in production Firestore.
                </>
              )}
            </Typography>
          </Alert>

          <FormControlLabel
            control={
              <Checkbox
                checked={popupEnabled}
                onChange={(e) => setPopupEnabled(e.target.checked)}
              />
            }
            label="Enable popup message"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            multiline
            minRows={4}
            maxRows={12}
            label="Popup Message"
            placeholder="e.g., 👋 Kia ora! I'm Adrift's chatbot..."
            value={popupMessage}
            onChange={(e) => setPopupMessage(e.target.value)}
            disabled={!popupEnabled}
            helperText="Line breaks and spacing will be preserved in the popup"
            sx={{ mb: 2 }}
          />
        </CardContent>
        <CardActions>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Save />}
            onClick={saveWidgetConfig}
            disabled={savingWidget}
          >
            {savingWidget ? 'Saving...' : 'Save Widget Config'}
          </Button>
        </CardActions>
      </Card>

      {/* Gmail Status */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailOutlined />
            Gmail API Status
          </Typography>

          {loadingGmailStatus ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Checking Gmail status...</Typography>
            </Box>
          ) : gmailStatus ? (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {gmailStatus.gmailConfigured ? (
                  <Chip label="✅ Configured" color="success" size="small" />
                ) : (
                  <Chip label="❌ Not Configured" color="error" size="small" />
                )}

                {gmailStatus.connectionTest?.success ? (
                  <Chip label="✅ Connected" color="success" size="small" />
                ) : gmailStatus.gmailConfigured ? (
                  <Chip label="❌ Connection Failed" color="error" size="small" />
                ) : null}
              </Box>

              {gmailStatus.connectionTest?.success && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Email:</strong> {gmailStatus.connectionTest.emailAddress}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Inbox:</strong> {gmailStatus.inboxInfo?.messagesTotal || 0} total messages,
                    {gmailStatus.inboxInfo?.messagesUnread || 0} unread
                  </Typography>
                </Box>
              )}

              {gmailStatus.connectionTest?.error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Connection Error:</strong> {gmailStatus.connectionTest.error}
                  </Typography>
                </Alert>
              )}

              {!gmailStatus.gmailConfigured && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Gmail API is not configured. Email checking is disabled. Contact the administrator to set up Gmail API credentials.
                  </Typography>
                </Alert>
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Unable to load Gmail status
            </Typography>
          )}

          <Button
            variant="outlined"
            size="small"
            onClick={loadGmailStatus}
            disabled={loadingGmailStatus}
            sx={{ mt: 1 }}
          >
            Refresh Status
          </Button>
        </CardContent>
      </Card>

      {/* Email Instructions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Email />
            Automatic Email Updates
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Status messages can be updated automatically by sending emails to{' '}
            <strong>adriftnzapp@gmail.com</strong>.
          </Typography>

          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>How it works:</strong>
          </Typography>
          <Typography variant="body2" component="div" sx={{ mb: 2 }}>
            • Send an email with your status message in the body<br/>
            • Only emails from approved senders will be processed<br/>
            • The system automatically checks for new emails <strong>every 15 minutes</strong><br/>
            • You can also manually check using the button below
          </Typography>

          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Approved senders:</strong>
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip label="amanda@adriftnz.co.nz" size="small" />
            <Chip label="geoff@emertech.nz" size="small" />
            <Chip label="opsmanager@adriftnz.co.nz" size="small" />
            <Chip label="stewart@adriftnz.co.nz" size="small" />
            <Chip label="robyn@adriftnz.co.nz" size="small" />
          </Box>
        </CardContent>
        <CardActions>
          <Button
            variant="outlined"
            startIcon={<EmailOutlined />}
            onClick={checkEmailForUpdates}
            disabled={checkingEmail}
          >
            {checkingEmail ? 'Checking...' : 'Check Email'}
          </Button>
        </CardActions>
      </Card>

      {/* Information */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Note:</strong> Status messages are stored in Firestore and automatically revert to the default based on NZT schedule rules.
          The email checking system runs automatically in the background, and the reversion checker runs every 5 minutes.
          {window.location.hostname.includes('staging') && (
            <>
              <br/><br/>
              <strong>Staging Mode:</strong> This staging admin dashboard is loading status data from the PRODUCTION environment.
            </>
          )}
        </Typography>
      </Alert>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}>
        <DialogTitle>Confirm Status Update</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to update the status message to:
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
            <Typography variant="body1" sx={{ fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>
              "{newStatus}"
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmSave}
            variant="contained"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Confirm Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default StatusPage;
